import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, exhaustMap, map, of, switchMap, withLatestFrom } from 'rxjs';
import * as SessionActions from './session.actions';
import { selectSessions, selectSessionsQuery } from './session.selectors';
import { HttpStatusCode } from '@angular/common/http';
import { SessionService } from '../services/session.service';
import { SessionDto, SessionUpdateResponseDto } from '@shared/dto';
import { listResultActions$ } from '@shared/utils/pagination.utils';

@Injectable()
export class SessionEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly sessionService = inject(SessionService);

  readSessions$ = createEffect(() => this.actions$.pipe(
    ofType(SessionActions.readSessions),
    exhaustMap(({ query }) =>
      listResultActions$(
        // The session library only lists TEMPLATE sessions — non-template ones exist
        // only inside a program/assignment and shouldn't appear here.
        query
          ? this.sessionService.read(undefined, { params: { isTemplate: true }, pagination: query })
          : this.sessionService.read(undefined, { params: { isTemplate: true } }),
        query,
        ({ data, total }) => SessionActions.readSessionsSuccess({ data, total }),
        (error) => SessionActions.readSessionsFailure({ error }),
      )),
  ));

  // Detail: resolve from the already-loaded list first (no HTTP), otherwise fetch
  // the single entity. The store is the source of truth that both the detail page
  // and the breadcrumb read from.
  readSession$ = createEffect(() => this.actions$.pipe(
    ofType(SessionActions.readSession),
    withLatestFrom(this.store.select(selectSessions)),
    switchMap(([{ sessionId }, list]) => {
      const cached = list?.find((session) => session.id === sessionId);
      if (cached) {
        return of(SessionActions.readSessionSuccess({ session: cached }));
      }

      return this.sessionService.readOne({ sessionId }).pipe(
        map((session) => SessionActions.readSessionSuccess({ session })),
        catchError((error) => of(SessionActions.readSessionFailure({ error }))),
      );
    }),
  ));

  createSession$ = createEffect(() => this.actions$.pipe(
    ofType(SessionActions.createSession),
    exhaustMap(({ dto }) => this.sessionService.create(dto).pipe(
      map((session: SessionDto) => SessionActions.createSessionSuccess({ session })),
      catchError((error) => of(SessionActions.createSessionFailure({ error })))
    ))
  ));

  updateSession$ = createEffect(() => this.actions$.pipe(
    ofType(SessionActions.updateSession),
    exhaustMap(({ sessionId, dto }) => this.sessionService.update({ sessionId }, dto).pipe(
      map((response: Partial<SessionUpdateResponseDto>) => {
        const { propagation, ...session } = response;
        return SessionActions.updateSessionSuccess({ session: session as Partial<SessionDto>, propagation });
      }),
      catchError((error) => {
        // 428 = the edit would rewrite events already scheduled from this session and
        // the request carried no `confirm`. Not a failure: fetch the consequences so the
        // page can show them and let the practitioner decide. Any other status — and a
        // references fetch that itself fails — stays an ordinary failure.
        if (error?.status !== HttpStatusCode.PreconditionRequired) {
          return of(SessionActions.updateSessionFailure({ error }));
        }
        return this.sessionService.references(sessionId).pipe(
          map((references) => SessionActions.updateSessionRequiresConfirmation({
            sessionId,
            dto,
            message: error.message,
            references,
          })),
          catchError(() => of(SessionActions.updateSessionFailure({ error }))),
        );
      }),
    ))
  ));

  deleteSession$ = createEffect(() => this.actions$.pipe(
    ofType(SessionActions.deleteSession),
    exhaustMap(({ sessionId, confirm }) => this.sessionService.delete(
      { sessionId },
      confirm ? { params: { confirm: 'true' } } : undefined,
    ).pipe(
      map((session: SessionDto) => SessionActions.deleteSessionSuccess({ session })),
      catchError((error) => {
        // 428 = active events exist; not a failure, let the page confirm and resend
        // `confirm=true`. Any other status (403 not-owner, 409 referenced in a live program)
        // stays a failure — its message is surfaced by the toast.
        if (error?.status === HttpStatusCode.PreconditionRequired) {
          return of(SessionActions.deleteSessionRequiresConfirmation({ sessionId, message: error.message }));
        }
        return of(SessionActions.deleteSessionFailure({ error }));
      })
    ))
  ));

  restoreSession$ = createEffect(() => this.actions$.pipe(
    ofType(SessionActions.restoreSession),
    exhaustMap(({ sessionId }) => this.sessionService.restore({ sessionId }).pipe(
      map((session: SessionDto) => SessionActions.restoreSessionSuccess({ session })),
      catchError((error) => of(SessionActions.restoreSessionFailure({ error })))
    ))
  ));

  // After a mutation, refetch the current page so counts/pagination stay correct.
  reloadAfterMutation$ = createEffect(() => this.actions$.pipe(
    ofType(
      SessionActions.createSessionSuccess,
      SessionActions.updateSessionSuccess,
      SessionActions.deleteSessionSuccess,
      SessionActions.restoreSessionSuccess,
    ),
    withLatestFrom(this.store.select(selectSessionsQuery)),
    map(([, query]) => SessionActions.readSessions(query ?? undefined)),
  ));
}
